/**
 * Park Manager Portal - Report Issue Page
 * Mobile-first form for park managers to log new jobs
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  AlertCircle, 
  Upload, 
  X, 
  Camera,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { PortalLayout } from '../components/PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ISSUE_TYPES = [
  'Maintenance',
  'Electrical',
  'Plumbing',
  'Gas',
  'Structural',
  'Fire Safety',
  'Water Hygiene',
  'HVAC',
  'Other'
];

const PRIORITIES = [
  { value: 'Low', label: 'Low', description: 'Can wait a few days' },
  { value: 'Medium', label: 'Medium', description: 'Within 48 hours' },
  { value: 'High', label: 'High', description: 'Within 24 hours' },
  { value: 'Urgent', label: 'Urgent', description: 'Emergency - immediate action' }
];

export default function PortalReportPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [createdJobId, setCreatedJobId] = useState(null);
  
  const [park, setPark] = useState(null);
  const [assets, setAssets] = useState([]);
  
  const [formData, setFormData] = useState({
    unit_pitch: '',
    issue_type: '',
    description: '',
    priority: 'Medium',
    contact_name: '',
    contact_phone: '',
    asset_id: ''
  });
  
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    fetchParkAndAssets();
  }, []);

  const fetchParkAndAssets = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch park info
      const parkRes = await fetch(`${API_URL}/api/park-portal/park`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (parkRes.ok) {
        const parks = await parkRes.json();
        if (parks.length > 0) setPark(parks[0]);
      }
      
      // Fetch assets/units
      const assetsRes = await fetch(`${API_URL}/api/park-portal/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (assetsRes.ok) {
        const data = await assetsRes.json();
        setAssets(data);
      }
    } catch (error) {
      console.error('Failed to fetch park data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setPhotos([...photos, ...newPhotos]);
  };

  const removePhoto = (index) => {
    const newPhotos = [...photos];
    URL.revokeObjectURL(newPhotos[index].preview);
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.unit_pitch || !formData.issue_type || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Create the job
      const res = await fetch(`${API_URL}/api/park-portal/jobs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create job');
      }
      
      const job = await res.json();
      setCreatedJobId(job.id);
      
      // Upload photos if any
      if (photos.length > 0) {
        for (const photo of photos) {
          const photoForm = new FormData();
          photoForm.append('file', photo.file);
          
          await fetch(`${API_URL}/api/park-portal/jobs/${job.id}/attachments`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: photoForm
          });
        }
      }
      
      setSubmitted(true);
      toast.success('Issue reported successfully!');
      
    } catch (error) {
      toast.error(error.message || 'Failed to submit issue');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <PortalLayout>
        <div className="p-4 pb-20 md:p-6 md:pb-6 flex items-center justify-center min-h-[60vh]">
          <Card className="border-0 shadow-sm max-w-md w-full text-center">
            <CardContent className="p-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold mb-2">Issue Reported!</h2>
              <p className="text-slate-600 mb-6">
                Your issue has been logged and the team has been notified. You can track its progress in the Jobs section.
              </p>
              <div className="space-y-2">
                <Button 
                  className="w-full bg-[#1F7A78]"
                  onClick={() => navigate(`/portal/jobs?selected=${createdJobId}`)}
                >
                  View Job Details
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/portal')}
                >
                  Back to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="p-4 pb-20 md:p-6 md:pb-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-[#1F7A78]" />
            Report Issue
          </h1>
          <p className="text-sm text-slate-500 mt-1">Log a new maintenance issue for your park</p>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="border-0 shadow-sm mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Location Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Park (Auto-filled) */}
              <div>
                <Label className="text-sm">Park</Label>
                <Input 
                  value={park?.name || 'Loading...'} 
                  disabled 
                  className="bg-slate-50"
                />
              </div>

              {/* Unit/Pitch */}
              <div>
                <Label className="text-sm">Unit / Pitch *</Label>
                {assets.length > 0 ? (
                  <Select 
                    value={formData.asset_id || formData.unit_pitch}
                    onValueChange={(val) => {
                      const asset = assets.find(a => a.id === val);
                      setFormData({
                        ...formData, 
                        asset_id: val,
                        unit_pitch: asset?.unit_number || asset?.name || val
                      });
                    }}
                  >
                    <SelectTrigger data-testid="unit-select">
                      <SelectValue placeholder="Select unit/pitch" />
                    </SelectTrigger>
                    <SelectContent>
                      {assets.map(asset => (
                        <SelectItem key={asset.id} value={asset.id}>
                          {asset.unit_number || asset.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="other">Other (specify below)</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={formData.unit_pitch}
                    onChange={(e) => setFormData({...formData, unit_pitch: e.target.value})}
                    placeholder="e.g., Plot 10, Unit A5"
                    required
                    data-testid="unit-input"
                  />
                )}
                {formData.asset_id === 'other' && (
                  <Input
                    className="mt-2"
                    value={formData.unit_pitch}
                    onChange={(e) => setFormData({...formData, unit_pitch: e.target.value})}
                    placeholder="Enter unit/pitch reference"
                    required
                  />
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Issue Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Issue Type */}
              <div>
                <Label className="text-sm">Issue Type *</Label>
                <Select 
                  value={formData.issue_type}
                  onValueChange={(val) => setFormData({...formData, issue_type: val})}
                >
                  <SelectTrigger data-testid="issue-type-select">
                    <SelectValue placeholder="Select issue type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ISSUE_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Priority */}
              <div>
                <Label className="text-sm">Priority *</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {PRIORITIES.map(p => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setFormData({...formData, priority: p.value})}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        formData.priority === p.value 
                          ? 'border-[#1F7A78] bg-[#1F7A78]/5' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`priority-${p.value.toLowerCase()}`}
                    >
                      <span className={`font-medium text-sm ${
                        formData.priority === p.value ? 'text-[#1F7A78]' : ''
                      }`}>{p.label}</span>
                      <p className="text-xs text-slate-500">{p.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <Label className="text-sm">Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe the issue in detail..."
                  rows={4}
                  required
                  data-testid="description-input"
                />
              </div>

              {/* Photo Upload */}
              <div>
                <Label className="text-sm">Photos (Optional)</Label>
                <div className="mt-2">
                  <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-[#1F7A78] transition-colors">
                    <Camera className="h-5 w-5 text-slate-400" />
                    <span className="text-sm text-slate-600">Tap to add photos</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handlePhotoUpload}
                      data-testid="photo-upload"
                    />
                  </label>
                </div>
                {photos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {photos.map((photo, idx) => (
                      <div key={idx} className="relative w-20 h-20">
                        <img 
                          src={photo.preview} 
                          alt={photo.name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(idx)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Contact Details (Optional)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm">Contact Name</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                  placeholder="Name of person to contact"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <Label className="text-sm">Contact Phone</Label>
                <Input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  placeholder="Phone number"
                  data-testid="contact-phone-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full h-12 text-base bg-[#1F7A78] hover:bg-[#005a5c]"
            disabled={submitting}
            data-testid="submit-issue-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Issue'
            )}
          </Button>
        </form>
      </div>
    </PortalLayout>
  );
}
